'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { ApiError } from '@website/lib/api/apiError';

/** The server's own reason for a refusal, when it is a short single line. */
function serverReason(error: unknown): string | undefined {
  const reason = error instanceof ApiError ? error.serverMessage?.trim() : undefined;
  return reason && reason.length <= 200 && !reason.includes('\n') ? reason : undefined;
}

/** The copy for a failed API call's error code (`ApiErrors.<code>`), or `undefined` when it has none. */
export function useCodedErrorMessage() {
  const t = useTranslations('ApiErrors');
  return useCallback(
    (error: unknown): string | undefined => {
      const code = error instanceof ApiError ? error.code : undefined;
      return code && t.has(code as Parameters<typeof t.has>[0]) ? t(code as Parameters<typeof t>[0]) : undefined;
    },
    [t]
  );
}

/**
 * Turns a failed API call into copy: `ApiErrors.<code>` when the API sent a code that has copy;
 * with `serverReason`, the server's own short reason next (English, for business-rule refusals
 * that have no code yet); then `fallback` (the caller's own wording), or a generic message.
 */
export function useApiErrorMessage() {
  const t = useTranslations('ApiErrors');
  const coded = useCodedErrorMessage();
  return useCallback(
    (error: unknown, fallback?: string, options: { serverReason?: boolean } = {}): string =>
      coded(error) ?? (options.serverReason ? serverReason(error) : undefined) ?? fallback ?? t('generic'),
    [coded, t]
  );
}
